import { useState, useEffect } from 'react';
import { submitCareerForm } from '../../services/api.service';
import { loadReCAPTCHAv3 } from '../../utils/loadRecaptcha';

const SITE_KEY = process.env.REACT_APP_SITE_KEY;

const CareerForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    address: '',
    email: '',
    contactNumber: '',
    jobCategory: '',
    message: '',
    resume: null,
    photo: null
  });

  const [photoPreview, setPhotoPreview] = useState(null); // 👈 New state for image preview
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ success: false, message: '' });

  useEffect(() => {
    loadReCAPTCHAv3(SITE_KEY).catch(err => {
      console.error('reCAPTCHA load error:', err);
    });
  }, []);

  const validateFile = (file, fieldName) => {
    const maxSize = 2 * 1024 * 1024; // 2MB
    if (fieldName === 'resume') {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];
      if (!allowedTypes.includes(file.type)) {
        return 'Only PDF and DOC files are allowed';
      }
    }

    if (file.size > maxSize) {
      return 'File size should be less than 2MB';
    }

    return '';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = 'Full Name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact number is required';
    } else if (!/^\d{10}$/.test(formData.contactNumber)) {
      newErrors.contactNumber = 'Enter a valid 10-digit number';
    }

    if (!formData.jobCategory.trim()) newErrors.jobCategory = 'Job category is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    if (!formData.resume) {
      newErrors.resume = 'Resume is required';
    } else {
      const resumeError = validateFile(formData.resume, 'resume');
      if (resumeError) newErrors.resume = resumeError;
    }

    if (!formData.photo) {
      newErrors.photo = 'Photo is required';
    } else {
      const photoError = validateFile(formData.photo, 'photo');
      if (photoError) newErrors.photo = photoError;
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const grecaptcha = window.grecaptcha;
      if (!grecaptcha) throw new Error('reCAPTCHA not loaded');

      const token = await grecaptcha.execute(SITE_KEY, { action: 'submit' });

      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });
      formDataToSend.append('captchaToken', token);

      const response = await submitCareerForm(Object.fromEntries(formDataToSend.entries()));

      if (response.status === 'SUCCESS') {
        setSubmitStatus({ success: true, message: 'Form submitted successfully!' });
        setFormData({
          fullName: '',
          address: '',
          email: '',
          contactNumber: '',
          jobCategory: '',
          message: '',
          resume: null,
          photo: null
        });
        setPhotoPreview(null);
      } else {
        setSubmitStatus({ success: false, message: response.message || 'Submission failed. Please try again.' });
      }
    } catch (err) {
      console.error('Form submission failed:', err);
      setSubmitStatus({ success: false, message: 'Submission failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      [fieldName]: file
    }));
    setErrors(prev => ({
      ...prev,
      [fieldName]: ''
    }));

    if (fieldName === 'photo' && file) {
      setPhotoPreview(URL.createObjectURL(file)); // 👈 Generate preview URL
    }
  };

  const inputClasses = (fieldName) =>
    `w-full p-3 rounded-md bg-gray-100 border-0 ${errors[fieldName] ? 'ring-2 ring-red-500' : ''}`;

  return (
    <div className="md:w-1/2">
      <h3 className="text-2xl font-semibold mb-6">Apply for a career opportunity with us</h3>

      {submitStatus.message && (
        <div className={`mb-6 p-4 rounded-md ${submitStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {submitStatus.message}
        </div>
      )}

      <form className="space-y-4" onSubmit={handleSubmit}>
        <input name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Full Name *" className={inputClasses('fullName')} />
        {errors.fullName && <p className="text-red-500 text-sm">{errors.fullName}</p>}

        <textarea name="address" value={formData.address} onChange={handleChange} placeholder="Address *" className={`${inputClasses('address')} h-24`} />
        {errors.address && <p className="text-red-500 text-sm">{errors.address}</p>}

        <input name="email" value={formData.email} onChange={handleChange} placeholder="Email *" className={inputClasses('email')} />
        {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}

        <input name="contactNumber" value={formData.contactNumber} onChange={handleChange} placeholder="Contact Number *" className={inputClasses('contactNumber')} />
        {errors.contactNumber && <p className="text-red-500 text-sm">{errors.contactNumber}</p>}

        <select name="jobCategory" value={formData.jobCategory} onChange={handleChange} className={inputClasses('jobCategory')}>
          <option value="">Select Job Category *</option>
          <option value="retail">Retail Operations</option>
          <option value="management">Management</option>
          <option value="logistics">Logistics</option>
        </select>
        {errors.jobCategory && <p className="text-red-500 text-sm">{errors.jobCategory}</p>}

        <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Message *" className={`${inputClasses('message')} h-24`} />
        {errors.message && <p className="text-red-500 text-sm">{errors.message}</p>}

        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center bg-gray-100 p-3 rounded-md">
              <span>Upload resume *</span>
              <input
                type="file"
                name="resume"
                onChange={(e) => handleFileChange(e, 'resume')}
                accept=".pdf,.doc,.docx"
                className="hidden"
                id="resume"
              />
              <label htmlFor="resume" className="bg-white px-4 py-1 rounded cursor-pointer">
                {formData.resume ? formData.resume.name : 'Choose File'}
              </label>
            </div>
            {errors.resume && <p className="text-red-500 text-sm mt-1">{errors.resume}</p>}
          </div>

          <div>
            <div className="flex justify-between items-center bg-gray-100 p-3 rounded-md">
              <span>Add photo *</span>
              <input
                type="file"
                name="photo"
                onChange={(e) => handleFileChange(e, 'photo')}
                accept="image/*"
                className="hidden"
                id="photo"
              />
              <label htmlFor="photo" className="bg-white px-4 py-1 rounded cursor-pointer">
                {formData.photo ? formData.photo.name : 'Choose File'}
              </label>
            </div>
            {photoPreview && (
              <div className="mt-2">
                <img
                  src={photoPreview}
                  alt="Preview"
                  className="w-32 h-32 object-cover rounded-md"
                />
              </div>
            )}
            {errors.photo && <p className="text-red-500 text-sm mt-1">{errors.photo}</p>}
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-3 rounded-md text-white ${isSubmitting ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-600'}`}
        >
          {isSubmitting ? 'Submitting...' : 'SUBMIT'}
        </button>
      </form >
    </div >
  );
};

export default CareerForm;
